import { useState, useEffect, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { grafanaQueryApiRef } from '../api/GrafanaQueryApi';
import { transformToTimeSeries } from '../utils/transformDataFrame';
import type { TimeSeriesPoint } from '../api/types';

/**
 * Fetches a lightweight 7-day overview metric for the timeline sparkline.
 * The data is independent of the brush selection — always shows the full 7d window
 * so users can see where activity is when selecting a custom range.
 */
export function useTimelineData(service: string): {
  data: TimeSeriesPoint[];
  loading: boolean;
} {
  const api = useApi(grafanaQueryApiRef);
  const [data, setData] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(
    () => [
      {
        expr: `sum(rate(http_server_request_duration_seconds_count{service_name=~"${service}"}[5m]))`,
        refId: 'timelineOverview',
        format: 'time_series' as const,
        maxDataPoints: 200,
      },
    ],
    [service],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api
      .queryPrometheus(query, { from: 'now-7d', to: 'now' })
      .then(result => {
        if (cancelled) return;
        const series = transformToTimeSeries(result, 'timelineOverview');
        setData(series[0]?.points ?? []);
      })
      .catch(() => {
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, query]);

  return { data, loading };
}
